// Galería de imágenes PROPIAS de un disco (fotos subidas por el usuario a S3).
//
// Permite: subir una foto (eligiendo archivo o tomándola con la cámara del
// dispositivo), marcarla como carátula principal, reordenarla (subir/bajar) y
// borrarla. Es el fallback para cuando no hay portada de Discogs o el usuario
// quiere su propia imagen. Las imágenes se guardan en S3 como el resto
// (presigned PUT) y se listan vía `GET /records/{id}/images`.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { AlertMessage, ButtonSpinner, Icon, Spinner } from '../ui/components.js';
import type { RecordImage } from '../types.js';

/** Claves i18n de los tipos de imagen que el usuario puede subir (no `discogs`). */
const KIND_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'front', labelKey: 'recordImages.front' },
  { value: 'back', labelKey: 'recordImages.back' },
  { value: 'label', labelKey: 'recordImages.label' },
];

export function RecordImageGallery({
  recordId,
  primaryUrl,
}: {
  recordId: string;
  /** URL de la carátula principal actual del disco (para resaltarla al cargar). */
  primaryUrl?: string | null;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [images, setImages] = useState<RecordImage[]>([]);
  const [primaryId, setPrimaryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState<string>('front');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.records.images.list(recordId);
      setImages(res.items);
      // Marca como principal la imagen cuya URL coincide con la carátula actual
      // del disco (solo si aún no se eligió una en esta sesión).
      setPrimaryId((prev) => prev ?? res.items.find((i) => primaryUrl && i.url === primaryUrl)?.id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recordImages.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [api, recordId, primaryUrl, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onFileSelected(file: File) {
    setUploading(true);
    setError(null);
    try {
      const contentType = file.type || 'image/jpeg';
      // 1) URL prefirmada. 2) PUT directo a S3. 3) asociar (crea record_image).
      const { upload_url, s3_key } = await api.records.images.createUploadUrl(recordId, {
        content_type: contentType,
        kind,
      });
      const put = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'content-type': contentType },
        body: file,
      });
      if (!put.ok) throw new Error(t('recordImages.errUploadS3'));
      // Si es la primera imagen del disco, se marca como principal.
      const makePrimary = images.length === 0;
      await api.records.images.associate(recordId, {
        s3_key,
        content_type: contentType,
        byte_size: file.size,
        kind,
        make_primary: makePrimary,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recordImages.errUpload'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function makePrimary(image: RecordImage) {
    setBusyId(image.id);
    setError(null);
    try {
      await api.records.images.setPrimary(recordId, image.id);
      setPrimaryId(image.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recordImages.errPrimary'));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(image: RecordImage) {
    if (!confirm(t('recordImages.removeConfirm'))) return;
    setBusyId(image.id);
    setError(null);
    try {
      await api.records.images.remove(recordId, image.id);
      if (primaryId === image.id) setPrimaryId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recordImages.errRemove'));
    } finally {
      setBusyId(null);
    }
  }

  /** Mueve una imagen una posición (dir -1 = arriba, +1 = abajo) y persiste. */
  async function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= images.length) return;
    const next = [...images];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    setImages(next); // optimista
    setError(null);
    try {
      await api.records.images.reorder(
        recordId,
        next.map((i) => i.id),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recordImages.errReorder'));
      await load(); // revertir al estado real
    }
  }

  return (
    <div className="flex flex-col gap-space-sm">
      <AlertMessage message={error} />

      {/* Subir una foto propia (archivo o cámara). */}
      <div className="flex items-end gap-space-sm">
        <div className="flex flex-col gap-space-xs">
          <label className="field-label">{t('recordImages.type')}</label>
          <div className="field-slot">
            <select className="field-input !h-10" value={kind} onChange={(e) => setKind(e.target.value)}>
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFileSelected(file);
          }}
        />
        <button
          type="button"
          className="btn-primary"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? <ButtonSpinner /> : <Icon name="add_a_photo" className="text-[18px]" />}
          {uploading ? t('recordImages.uploading') : t('recordImages.upload')}
        </button>
      </div>

      {loading && <Spinner label={t('recordImages.loading')} />}

      {!loading && images.length === 0 && (
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          {t('recordImages.empty')}
        </p>
      )}

      {/* Lista de imágenes con acciones. */}
      {!loading && images.length > 0 && (
        <div className="grid grid-cols-2 gap-space-sm">
          {images.map((img, idx) => {
            const isPrimary = img.id === primaryId;
            return (
              <div
                key={img.id}
                className={`relative rounded-lg overflow-hidden border-2 ${
                  isPrimary ? 'border-primary-container' : 'border-border-subtle'
                }`}
              >
                <img src={img.url} alt={t('recordImages.imageAlt')} className="w-full aspect-square object-cover" />

                {isPrimary && (
                  <span className="absolute top-1 left-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-container text-surface-base font-badge-micro text-badge-micro uppercase tracking-wider">
                    <Icon name="star" className="text-[12px]" /> {t('recordImages.primary')}
                  </span>
                )}

                {/* Acciones. */}
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 p-1 bg-surface-base/70 backdrop-blur-sm">
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      aria-label={t('recordImages.moveUp')}
                      className="w-7 h-7 rounded flex items-center justify-center text-on-surface disabled:opacity-30"
                      disabled={idx === 0}
                      onClick={() => void move(idx, -1)}
                    >
                      <Icon name="arrow_upward" className="text-[16px]" />
                    </button>
                    <button
                      type="button"
                      aria-label={t('recordImages.moveDown')}
                      className="w-7 h-7 rounded flex items-center justify-center text-on-surface disabled:opacity-30"
                      disabled={idx === images.length - 1}
                      onClick={() => void move(idx, 1)}
                    >
                      <Icon name="arrow_downward" className="text-[16px]" />
                    </button>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {!isPrimary && (
                      <button
                        type="button"
                        aria-label={t('recordImages.makePrimary')}
                        className="w-7 h-7 rounded flex items-center justify-center text-primary disabled:opacity-40"
                        disabled={busyId === img.id}
                        onClick={() => void makePrimary(img)}
                      >
                        <Icon name="star" className="text-[16px]" />
                      </button>
                    )}
                    <button
                      type="button"
                      aria-label={t('recordImages.remove')}
                      className="w-7 h-7 rounded flex items-center justify-center text-priority-urgent disabled:opacity-40"
                      disabled={busyId === img.id}
                      onClick={() => void remove(img)}
                    >
                      <Icon name="delete" className="text-[16px]" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
